import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  Calendar1, ChevronLeft, ChevronRight, X, Tag, Clock, Video, MapPin, 
  Users, ArrowRight, Info, Calendar as CalendarIcon, DollarSign,
  Ticket, Calendar as CalendarCheck, AlertCircle, Globe 
} from 'lucide-react';


const EventCalendar = ({ events, isOpen, onClose, onEventClick }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
  
    // Reset selected date when modal closes
    useEffect(() => {
      if (!isOpen) {
        setSelectedDate(null);
        setHoveredEvent(null);
      }
    }, [isOpen]);
  
    const getDaysInMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };
  
    const getFirstDayOfMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };
  
    const formatEventTime = (date) => {
      return new Date(date).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    };
  
    const getEventColor = (eventType) => {
        const colors = {
          reunion: {
            bg: 'bg-gradient-to-br from-purple-50 to-fuchsia-50',
            text: 'text-purple-700',
            border: 'border-purple-200',
            hover: 'hover:bg-gradient-to-br hover:from-purple-100 hover:to-fuchsia-100',
            dot: 'bg-purple-500'
          },
          workshop: {
            bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
            text: 'text-blue-700',
            border: 'border-blue-200',
            hover: 'hover:bg-gradient-to-br hover:from-blue-100 hover:to-cyan-100',
            dot: 'bg-blue-500'
          },
          seminar: {
            bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
            text: 'text-green-700',
            border: 'border-green-200',
            hover: 'hover:bg-gradient-to-br hover:from-green-100 hover:to-emerald-100',
            dot: 'bg-green-500'
          },
          other: {
            bg: 'bg-gradient-to-br from-gray-50 to-slate-50',
            text: 'text-gray-700',
            border: 'border-gray-200',
            hover: 'hover:bg-gradient-to-br hover:from-gray-100 hover:to-slate-100',
            dot: 'bg-gray-500'
          }
        };
        return colors[eventType] || colors.other;
      };
    
      const getDayClassName = (dayDate, dayEvents) => {
        const isToday = dayDate.toDateString() === new Date().toDateString();
        const isSelected = selectedDate && dayDate.toDateString() === selectedDate.toDateString();
        const hasEvents = dayEvents.length > 0;
    
        let baseClass = "relative h-16 border transition-all duration-200 rounded-2xl ";
        
        if (isSelected) {
          return `${baseClass} bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg scale-95 
            transform hover:shadow-xl`;
        }
        
        if (hasEvents) {
          const eventTypes = [...new Set(dayEvents.map(e => e.type))];
          if (eventTypes.length > 1) {
            return `${baseClass} bg-gradient-to-br from-indigo-50 to-purple-50 hover:from-indigo-100 
              hover:to-purple-100 border-indigo-200`;
          }
          const color = getEventColor(eventTypes[0]);
          return `${baseClass} ${color.bg} ${color.hover} border-${color.border}`;
        }
        
        if (isToday) {
          return `${baseClass} bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200`;
        }
    
        return `${baseClass} hover:bg-gray-50 text-gray-400`;
      };
    // Get events for a specific date
    const getEventsForDate = (date) => {
      return events.filter(event => {
        const eventDate = new Date(event.schedule.startDate).toDateString();
        return eventDate === date.toDateString();
      });
    };
  
   
  
    return (
        <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[1000] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              />
  
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-gradient-to-br from-gray-50 to-white w-full max-w-7xl 
                  rounded-3xl shadow-2xl overflow-hidden border border-gray-200/50"
              >
                {/* Premium Header */}
                <div className="px-8 py-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm">
                        <Calendar1 className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Event Calendar</h2>
                        <p className="text-blue-100 mt-1">
                          {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                    >
                      <X className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>
  
                <div className="flex divide-x divide-gray-200">
                  {/* Enhanced Calendar Grid */}
                  <div className="w-2/3 p-8">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={() => setCurrentDate(new Date())}
                          className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 
                            text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-colors"
                        >
                          Today
                        </button>
                        <button
                          onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                      </div>
                    </div>
  
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-4 mb-4">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-sm font-semibold text-gray-500 text-center">
                          {day}
                        </div>
                      ))}
                    </div>
  
                    <div className="grid grid-cols-7 gap-4">
                      {Array.from({ length: getFirstDayOfMonth(currentDate) }).map((_, i) => (
                        <div key={`empty-${i}`} className="h-16" />
                      ))}
                      
                      {Array.from({ length: getDaysInMonth(currentDate) }).map((_, i) => {
                        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1);
                        const dayEvents = getEventsForDate(date);
                        
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedDate(date)}
                            className={getDayClassName(date, dayEvents)}
                          >
                            <div className="absolute top-2 right-3 text-sm font-medium">
                              {i + 1}
                            </div>
                            {dayEvents.length > 0 && (
                              <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                                <div className="flex space-x-1">
                                  {dayEvents.slice(0, 3).map((event, index) => (
                                    <div
                                      key={index}
                                      className={`w-1.5 h-1.5 rounded-full ${getEventColor(event.type).dot}`}
                                    />
                                  ))}
                                  {dayEvents.length > 3 && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                  )}
                                </div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
  
                  {/* Events List */}
                  <div className="w-1/3 bg-gray-50 p-6 overflow-y-auto">
                    {selectedDate ? (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Events for {selectedDate.toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </h3>
                        <div className="space-y-4">
                          {getEventsForDate(selectedDate).length > 0 ? (
                            getEventsForDate(selectedDate).map((event) => (
                              <motion.div
                                key={event._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`p-4 rounded-xl border ${getEventColor(event.type).border} 
                                  ${getEventColor(event.type).bg} cursor-pointer 
                                  ${getEventColor(event.type).hover} transition-all duration-200`}
                                onClick={() => onEventClick(event._id)}
                                onMouseEnter={() => setHoveredEvent(event)}
                                onMouseLeave={() => setHoveredEvent(null)}
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full 
                                      text-xs font-medium ${getEventColor(event.type).bg} 
                                      ${getEventColor(event.type).text} mb-2`}
                                    >
                                      <Tag className="h-3 w-3 mr-1" />
                                      {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                                    </span>
                                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                                    
                                    <div className="mt-2 space-y-1">
                                      <div className="flex items-center text-sm text-gray-600">
                                        <Clock className="h-4 w-4 mr-1.5" />
                                        {formatEventTime(event.schedule.startDate)}
                                      </div>
                                      
                                      <div className="flex items-center text-sm text-gray-600">
                                        {event.venue.isVirtual ? (
                                          <>
                                            <Video className="h-4 w-4 mr-1.5" />
                                            Virtual Event
                                          </>
                                        ) : (
                                          <>
                                            <MapPin className="h-4 w-4 mr-1.5" />
                                            {event.venue.name}
                                          </>
                                        )}
                                      </div>
                                      
                                      <div className="flex items-center text-sm text-gray-600">
                                        <Users className="h-4 w-4 mr-1.5" />
                                        {event.registrations.length} registered
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {event === hoveredEvent && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <button
                                      className="w-full flex items-center justify-center px-4 py-2 
                                        text-sm font-medium text-blue-600 hover:text-blue-700"
                                    >
                                      View Details
                                      <ArrowRight className="h-4 w-4 ml-1.5" />
                                    </button>
                                  </div>
                                )}
                              </motion.div>
                            ))
                          ) : (
                            <div className="text-center py-8">
                              <Info className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <p className="text-gray-500">No events scheduled for this date</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <CalendarIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">Select a date to view events</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    );
  };

export default EventCalendar